import { useState, useCallback } from 'react';
import * as walletApi from '../../api/wallet/wallet.api';
import toast from 'react-hot-toast';

/**
 * Hook that wraps all user-facing wallet API calls.
 * Uses plain Axios (no React Query) with local loading/error state.
 */
export const useWallets = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const exec = useCallback(async (fn, ...args) => {
        setLoading(true);
        setError(null);
        try {
            const result = await fn(...args);
            return result;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchWallets = useCallback(() => exec(walletApi.listWallets), [exec]);

    const fetchBalance = useCallback(
        (currencyCode) => exec(walletApi.getBalance, currencyCode),
        [exec]
    );

    const fetchAllBalances = useCallback(
        (currencyCode) => exec(walletApi.getAllBalances, currencyCode),
        [exec]
    );

    const checkBalance = useCallback(
        (params) => exec(walletApi.checkBalance, params),
        [exec]
    );

    const doTopUp = useCallback(
        async (data) => {
            const res = await exec(walletApi.topUp, data);
            toast.success('Top-up successful!');
            return res;
        },
        [exec]
    );

    const doTransfer = useCallback(
        async (data) => {
            const res = await exec(walletApi.transfer, data);
            toast.success('Transfer successful!');
            return res;
        },
        [exec]
    );

    const doRefund = useCallback(
        async (data) => {
            const res = await exec(walletApi.refundTransaction, data);
            toast.success('Refund processed!');
            return res;
        },
        [exec]
    );

    const fetchTransactions = useCallback(
        (params) => exec(walletApi.getTransactions, params),
        [exec]
    );

    const doWithdraw = useCallback(
        async (data) => {
            const res = await exec(walletApi.withdraw, data);
            toast.success('Withdrawal request submitted!');
            return res;
        },
        [exec]
    );

    const fetchUsers = useCallback((params) => exec(walletApi.getUsers, params), [exec]);

    const fetchCurrencies = useCallback(() => exec(walletApi.getCurrencies), [exec]);

    return {
        loading,
        error,
        fetchWallets,
        fetchBalance,
        fetchAllBalances,
        checkBalance,
        doTopUp,
        doTransfer,
        doRefund,
        fetchTransactions,
        doWithdraw,
        fetchUsers,
        fetchCurrencies,
    };
};
